import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import type { Badge } from '@/types/database';

/**
 * App-level badge catalog so rendering components (e.g. Byline) can resolve
 * a flair slug to its full badge row (icon, color, tier) without refetching
 * per row. Loaded once on mount.
 */
interface CatalogValue {
  bySlug: Map<string, Badge>;
}

const Ctx = createContext<CatalogValue>({ bySlug: new Map() });

export function BadgeCatalogProvider({ children }: { children: ReactNode }) {
  const [badges, setBadges] = useState<Badge[]>([]);

  useEffect(() => {
    let alive = true;
    void supabase
      .from('badges')
      .select('*')
      .then(({ data }) => {
        if (!alive) return;
        setBadges(data ?? []);
      });
    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo<CatalogValue>(() => {
    const bySlug = new Map<string, Badge>();
    for (const b of badges) bySlug.set(b.slug, b);
    return { bySlug };
  }, [badges]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBadge(slug: string | null | undefined): Badge | null {
  const { bySlug } = useContext(Ctx);
  if (!slug) return null;
  return bySlug.get(slug) ?? null;
}
